import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { BlockDataForm, BlocksForm } from '@plone/volto/components/manage/Form';
import { SidebarPortal } from '@plone/volto/components';
import config from '@plone/volto/registry';
import EditBlockWrapper from '@plone/volto/components/manage/Blocks/Block/EditBlockWrapper';
import Schema from './schema';

/**
 * Tabs Edit v4 — gerenciamento de abas no CORPO, não na sidebar.
 *
 * Objects_list do Volto não preserva campos extras (id, blocks,
 * blocks_layout) — só serializa o que tá no schema interno. Por isso
 * o sidebar gerenciava só tabTitle, e qualquer rename/add corrompia
 * blocks. Agora o sidebar tem só eyebrow+title; a gestão das abas
 * (add/remove/rename/reorder) acontece no corpo.
 *
 * data.items = [{ id, tabTitle, blocks, blocks_layout }]
 */
const TabsEdit = (props) => {
  const {
    block,
    data,
    onChangeBlock,
    selected,
    pathname,
    metadata,
    properties,
    manage,
  } = props;

  // Cada aba precisa de >=1 sub-block (slate placeholder) para o BlocksForm
  // aninhado (isMainForm=false) ser editavel: nao ha botao "+" e o ENTER do
  // Volto so cria block novo a partir de um block existente.
  const makeEmptySlate = () => {
    const sid = uuid();
    return {
      blocks: { [sid]: { '@type': 'slate' } },
      blocks_layout: { items: [sid] },
    };
  };

  const items = Array.isArray(data?.items) ? data.items : [];
  const normalizedItems = items.map((it) => {
    const hasContent =
      it.blocks_layout &&
      Array.isArray(it.blocks_layout.items) &&
      it.blocks_layout.items.length > 0;
    const fallback = hasContent ? null : makeEmptySlate();
    return {
      ...it,
      id: it.id || uuid(),
      tabTitle: it.tabTitle || '',
      blocks: hasContent ? it.blocks : fallback.blocks,
      blocks_layout: hasContent ? it.blocks_layout : fallback.blocks_layout,
    };
  });

  const [currentIdx, setCurrentIdx] = useState(0);
  const [renameIdx, setRenameIdx] = useState(-1);
  const [selectedSubBlock, setSelectedSubBlock] = useState(null);

  React.useEffect(() => {
    if (currentIdx >= normalizedItems.length) {
      setCurrentIdx(Math.max(0, normalizedItems.length - 1));
    }
  }, [normalizedItems.length, currentIdx]);

  const persist = (newItems) => {
    onChangeBlock(block, { ...data, items: newItems });
  };

  const addTab = () => {
    const seeded = makeEmptySlate();
    const newTab = {
      id: uuid(),
      tabTitle: `Aba ${normalizedItems.length + 1}`,
      blocks: seeded.blocks,
      blocks_layout: seeded.blocks_layout,
    };
    persist([...normalizedItems, newTab]);
    setCurrentIdx(normalizedItems.length);
    setRenameIdx(normalizedItems.length);
  };

  const removeTab = (idx) => {
    if (!window.confirm('Remover esta aba e todo o conteúdo dela?')) return;
    const newItems = normalizedItems.filter((_, i) => i !== idx);
    persist(newItems);
    if (currentIdx >= newItems.length) {
      setCurrentIdx(Math.max(0, newItems.length - 1));
    }
  };

  const renameTab = (idx, newTitle) => {
    const newItems = [...normalizedItems];
    newItems[idx] = { ...newItems[idx], tabTitle: newTitle };
    persist(newItems);
  };

  const moveTab = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= normalizedItems.length) return;
    const newItems = [...normalizedItems];
    [newItems[idx], newItems[j]] = [newItems[j], newItems[idx]];
    persist(newItems);
    setCurrentIdx(j);
  };

  const updateTabContent = (idx, newProps) => {
    const newItems = [...normalizedItems];
    newItems[idx] = { ...newItems[idx], ...newProps };
    persist(newItems);
  };

  const currentTab = normalizedItems[currentIdx];
  const schema = Schema();

  return (
    <>
      <section className="ag-tabs ag-tabs--edit">
        {data.eyebrow && (
          <span className="ag-tabs__eyebrow">{data.eyebrow}</span>
        )}
        {data.title && <h2 className="ag-tabs__heading">{data.title}</h2>}

        <div className="tabs ag-tabs__nav ag-tabs__nav--edit" role="tablist">
          {normalizedItems.map((t, i) => (
            <div
              key={t.id}
              className={`ag-tabs__tab-wrap${i === currentIdx ? ' is-active' : ''}`}
            >
              {renameIdx === i ? (
                <input
                  type="text"
                  className="ag-tabs__rename-input"
                  autoFocus
                  defaultValue={t.tabTitle}
                  onBlur={(e) => {
                    renameTab(i, e.target.value || `Aba ${i + 1}`);
                    setRenameIdx(-1);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      renameTab(i, e.target.value || `Aba ${i + 1}`);
                      setRenameIdx(-1);
                    }
                    if (e.key === 'Escape') {
                      setRenameIdx(-1);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={i === currentIdx}
                  className={`tab ag-tabs__trigger${i === currentIdx ? ' is-active' : ''}`}
                  onClick={() => setCurrentIdx(i)}
                  onDoubleClick={() => setRenameIdx(i)}
                  title="Clique para selecionar, duplo-clique para renomear"
                >
                  {t.tabTitle || `Aba ${i + 1}`}
                </button>
              )}
              {i === currentIdx && (
                <div className="ag-tabs__tab-actions">
                  <button
                    type="button"
                    onClick={() => setRenameIdx(i)}
                    title="Renomear"
                    aria-label="Renomear"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    onClick={() => moveTab(i, -1)}
                    disabled={i === 0}
                    title="Mover esquerda"
                    aria-label="Mover esquerda"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => moveTab(i, 1)}
                    disabled={i === normalizedItems.length - 1}
                    title="Mover direita"
                    aria-label="Mover direita"
                  >
                    →
                  </button>
                  <button
                    type="button"
                    onClick={() => removeTab(i)}
                    title="Remover"
                    aria-label="Remover"
                    className="ag-tabs__tab-action--danger"
                  >
                    🗑
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            className="ag-tabs__add-btn"
            onClick={addTab}
            title="Adicionar aba"
          >
            + Aba
          </button>
        </div>

        <div className="ag-tabs__panel ag-tabs__panel--edit">
          {currentTab ? (
            <BlocksForm
              // Na edição, `properties` é a página; sem `metadata`, um bloco de
              // dentro da aba (a Galeria) não sabe o caminho.
              metadata={metadata || properties}
              properties={{
                blocks: currentTab.blocks,
                blocks_layout: currentTab.blocks_layout,
              }}
              manage={manage}
              selectedBlock={selected ? selectedSubBlock : null}
              blocksConfig={config.blocks.blocksConfig}
              isMainForm={false}
              stopPropagation={selectedSubBlock}
              onSelectBlock={(id) => setSelectedSubBlock(id)}
              onChangeFormData={(newFormData) => {
                updateTabContent(currentIdx, {
                  blocks: newFormData.blocks,
                  blocks_layout: newFormData.blocks_layout,
                });
              }}
              onChangeField={(id, value) => {
                if (id === 'blocks' || id === 'blocks_layout') {
                  updateTabContent(currentIdx, { [id]: value });
                }
              }}
              pathname={pathname}
            >
              {({ draginfo }, editBlock, blockProps) => (
                <EditBlockWrapper draginfo={draginfo} blockProps={blockProps}>
                  {editBlock}
                </EditBlockWrapper>
              )}
            </BlocksForm>
          ) : (
            <p className="ag-tabs__empty">
              Clique em <em>+ Aba</em> acima para começar.
            </p>
          )}
        </div>
      </section>

      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) => {
            onChangeBlock(block, { ...data, [id]: value });
          }}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

TabsEdit.propTypes = {
  block: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  pathname: PropTypes.string,
  metadata: PropTypes.object,
  properties: PropTypes.object,
  manage: PropTypes.bool,
};

export default TabsEdit;
